import {Injectable} from '@angular/core';
import {DBConfig, NgxIndexedDBService} from 'ngx-indexed-db';
import {environment} from "@env";
import {
    IChatRepositoryItem,
    IChatRepositoryMessage,
    IChatRepositorySetting, IChatRepositorySettingItem
} from "@core/office/office-chat-repository/office-chat-repository.types";
import {firstValueFrom} from "rxjs";
import {logger} from "@services/logger/logger.service";

const DB = environment.db;
export const SCHEMA_NAME_CHAT_MESSAGE = 'chatHistory';
export const SCHEMA_NAME_CHAT_SETTING = 'chatSetting';

export const dbConfig: DBConfig = {
    name: DB.name,
    version: DB.version,
    objectStoresMeta: [
        {
            store: SCHEMA_NAME_CHAT_MESSAGE,
            storeConfig: {keyPath: 'id', autoIncrement: true},
            storeSchema: [
                {name: 'id', keypath: 'id', options: {unique: true}},
                {name: 'version', keypath: 'version', options: {unique: false}},
                {name: 'history', keypath: 'history', options: {unique: false, multiEntry: true}}
            ]
        },
        {
            store: SCHEMA_NAME_CHAT_SETTING,
            storeConfig: {keyPath: 'user', autoIncrement: true},
            storeSchema: [
                {name: 'user', keypath: 'user', options: {unique: true}},
                {name: 'systemMessage', keypath: 'systemMessage', options: {unique: false}},
                {name: 'options', keypath: 'options', options: {unique: false, multiEntry: true}}
            ]
        }
    ]
};


@Injectable({
    providedIn: 'root'
})
export class OfficeChatRepository {
    private userID: string = '';
    private documentID: string = 'web';
    private historyKeySeparator = '--@@--';
    private settingKey = '';

    constructor(private db: NgxIndexedDBService) {
    }

    public defineHistoryKey(userID: string, documentID: string = this.documentID): string {
        this.userID = userID;
        this.documentID = documentID;
        return this.getHistoryKey();
    }

    public getHistoryKey(): string {
        if (!this.userID || !this.documentID) {
            throw new Error('Can not get history key,please check your value');
        }
        return `${this.userID}${this.historyKeySeparator}${this.documentID}`
    }

    public defineSettingKey(userID: string): void {
        this.settingKey = userID;
    }

    public getSettingKey(): string {
        if (!this.settingKey) {
            throw new Error('Can not get setting key,please check your value');
        }
        return this.settingKey;
    }

    public async readMessages(): Promise<IChatRepositoryMessage[]> {
        const key = this.getHistoryKey();
        const dataObserver = this.db.getByKey<IChatRepositoryItem>(SCHEMA_NAME_CHAT_MESSAGE, key);
        const data = await firstValueFrom<IChatRepositoryItem>(dataObserver)
        if (data && data.history) {
            return data.history
        }

        return []
    }

    public async writeMessages(
        messages: IChatRepositoryMessage[],
    ): Promise<IChatRepositoryMessage[]> {
        const data = {
            version: DB.version,
            id: this.getHistoryKey(),
            history: messages,
        }

        try {
            const dataObserver = this.db.update<IChatRepositoryItem>(SCHEMA_NAME_CHAT_MESSAGE, data);
            await firstValueFrom(dataObserver)
        } catch (e) {
            logger.error(e)
        }
        return messages;
    }

    public async deleteMessages(): Promise<void> {
        const key = this.getHistoryKey();
        this.db.deleteByKey(SCHEMA_NAME_CHAT_MESSAGE, key).subscribe(logger.log);
    }

    public async deleteSetting(): Promise<void> {
        const key = this.getSettingKey()
        this.db.deleteByKey(SCHEMA_NAME_CHAT_SETTING, key).subscribe(logger.log);
    }

    public async writeSetting(setting: IChatRepositorySetting): Promise<void> {
        const data = {
            ...setting,
            version: DB.version,
            user: this.getSettingKey(),
        }

        try {
            const dataObserver = this.db.update<IChatRepositorySettingItem>(SCHEMA_NAME_CHAT_SETTING, data);
            await firstValueFrom(dataObserver)
        } catch (e) {
            logger.error(e)
        }
    }

    public async readSetting(): Promise<IChatRepositorySetting | null> {
        const key = this.getSettingKey();
        const dataObserver = this.db.getByKey<IChatRepositorySettingItem>(SCHEMA_NAME_CHAT_SETTING, key);
        const data = await firstValueFrom<IChatRepositorySettingItem>(dataObserver)
        if (data) {
            return {
                systemMessage: data.systemMessage,
                options: data.options,
                quickActions: data.quickActions,
                skills: data.skills ?? []
            }
        }

        return null
    }
}
